import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield,
  ShieldOff,
  ShieldCheck,
  Plus,
  Search,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Upload
} from 'lucide-react';
import BulkUploadModal from '../components/account-lists/BulkUploadModal';

export const AccountLists = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ blacklist_activas: 0, whitelist_activas: 0 });
  const [filter, setFilter] = useState({ tipo: 'all', search: '' });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [newItem, setNewItem] = useState({
    cuenta_id: '',
    tipo: 'BLACKLIST',
    motivo: '',
    fecha_expiracion: ''
  });
  const [checkAccount, setCheckAccount] = useState('');
  const [checkResult, setCheckResult] = useState(null);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { activo: true };
      if (filter.tipo !== 'all') params.tipo = filter.tipo;
      if (filter.search) params.search = filter.search;

      const [itemsRes, statsRes] = await Promise.all([
        api.get('/account-lists/', { params }),
        api.get('/account-lists/stats')
      ]);
      
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar listas');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newItem.cuenta_id || !newItem.motivo) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      const payload = {
        cuenta_id: newItem.cuenta_id,
        tipo: newItem.tipo,
        motivo: newItem.motivo,
        fecha_expiracion: newItem.fecha_expiracion || null
      };

      await api.post('/account-lists/', payload);
      toast.success(`Cuenta agregada a ${newItem.tipo.toLowerCase()}`);
      setShowAddDialog(false);
      setNewItem({ cuenta_id: '', tipo: 'BLACKLIST', motivo: '', fecha_expiracion: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar cuenta');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta entrada?')) return;

    try {
      await api.delete(`/account-lists/${id}`);
      toast.success('Entrada eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCheck = async () => {
    if (!checkAccount) {
      toast.error('Ingresa un ID de cuenta');
      return;
    }

    try {
      const response = await api.get(`/account-lists/check/${checkAccount}`);
      setCheckResult(response.data);
    } catch (error) {
      toast.error('Error al verificar cuenta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando listas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="account-lists-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Blacklists / Whitelists
          </h1>
          <p className="text-slate-600 mt-1">
            Gestión de cuentas en listas de control
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            variant="outline"
            onClick={() => setShowBulkDialog(true)}
            style={{ borderColor: '#cbd5e1', color: '#1e293b', backgroundColor: '#ffffff' }}
            data-testid="bulk-upload-btn"
          >
            <Upload size={16} className="mr-2" />
            Carga Masiva
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            data-testid="add-account-btn"
          >
            <Plus size={16} className="mr-2" />
            Agregar Cuenta
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-xl">
              <ShieldOff size={24} className="text-red-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-red-600">{stats.blacklist_activas}</p>
              <p className="text-sm text-slate-500">En Blacklist</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-xl">
              <ShieldCheck size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-green-600">{stats.whitelist_activas}</p>
              <p className="text-sm text-slate-500">En Whitelist</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Shield size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.blacklist_activas + stats.whitelist_activas}</p>
              <p className="text-sm text-slate-500">Total en Listas</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Verificar cuenta */}
      <Card className="p-4 bg-white border-slate-200 mb-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Verificar Cuenta</h3>
        <div className="flex gap-3">
          <Input
            value={checkAccount}
            onChange={(e) => setCheckAccount(e.target.value)}
            placeholder="Ingresa CUIT o ID de cuenta..."
            className="flex-1"
            style={{ backgroundColor: '#ffffff' }}
            data-testid="check-account-input"
          />
          <Button onClick={handleCheck} style={{ backgroundColor: '#2563eb', color: '#ffffff' }}>
            <Search size={16} className="mr-2" />
            Verificar
          </Button>
        </div>
        
        {checkResult && (
          <div className="mt-4 p-4 rounded-lg bg-slate-50">
            <p className="font-medium text-slate-900 mb-2">Cuenta: {checkResult.cuenta_id}</p>
            <div className="flex gap-4">
              <div className={`flex items-center gap-2 ${checkResult.en_blacklist ? 'text-red-600' : 'text-slate-400'}`}>
                <ShieldOff size={20} />
                <span>{checkResult.en_blacklist ? `Blacklist: ${checkResult.blacklist_motivo}` : 'No en blacklist'}</span>
              </div>
              <div className={`flex items-center gap-2 ${checkResult.en_whitelist ? 'text-green-600' : 'text-slate-400'}`}>
                <ShieldCheck size={20} />
                <span>{checkResult.en_whitelist ? `Whitelist: ${checkResult.whitelist_motivo}` : 'No en whitelist'}</span>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Filtros */}
      <div className="flex gap-3 mb-4">
        <Select value={filter.tipo} onValueChange={(v) => setFilter({ ...filter, tipo: v })}>
          <SelectTrigger className="w-40" style={{ backgroundColor: '#ffffff' }}>
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="BLACKLIST">Blacklist</SelectItem>
            <SelectItem value="WHITELIST">Whitelist</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
          <Input
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder="Buscar por cuenta..."
            className="pl-10"
            style={{ backgroundColor: '#ffffff' }}
          />
        </div>
        <Button variant="outline" onClick={loadData} style={{ borderColor: '#cbd5e1' }}>
          <RefreshCw size={16} />
        </Button>
      </div>

      {/* Lista */}
      <Card className="bg-white border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cuenta / Cliente</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Motivo</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Fecha Alta</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Expira</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {items.length > 0 ? (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm text-slate-900">{item.cuenta_id}</div>
                      {item.nombre_cliente && <div className="text-xs text-slate-500 mt-0.5">{item.nombre_cliente}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span 
                        className={`px-2 py-1 rounded-full text-xs font-medium text-white ${
                          item.tipo === 'BLACKLIST' ? 'bg-red-500' : 'bg-green-500'
                        }`}
                      >
                        {item.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{item.motivo}</td>
                    <td className="px-4 py-3 text-center text-sm text-slate-500">
                      {new Date(item.created_at).toLocaleDateString('es-AR')}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-slate-500">
                      {item.fecha_expiracion 
                        ? new Date(item.fecha_expiracion).toLocaleDateString('es-AR')
                        : 'Permanente'
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    No hay cuentas en las listas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog Agregar */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="text-slate-900">Agregar Cuenta a Lista</DialogTitle>
            <DialogDescription className="text-slate-600">
              Agrega una cuenta a blacklist o whitelist
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Cuenta ID *</Label>
              <Input
                value={newItem.cuenta_id}
                onChange={(e) => setNewItem({ ...newItem, cuenta_id: e.target.value })}
                placeholder="CUIT, CVU, CBU..."
                style={{ backgroundColor: '#ffffff' }}
              />
            </div>
            <div>
              <Label className="text-slate-700">Tipo de Lista *</Label>
              <Select value={newItem.tipo} onValueChange={(v) => setNewItem({ ...newItem, tipo: v })}>
                <SelectTrigger style={{ backgroundColor: '#ffffff' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BLACKLIST">Blacklist (Bloquear)</SelectItem>
                  <SelectItem value="WHITELIST">Whitelist (Confiable)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Motivo *</Label>
              <Input
                value={newItem.motivo}
                onChange={(e) => setNewItem({ ...newItem, motivo: e.target.value })}
                placeholder="Razón para agregar a la lista..."
                style={{ backgroundColor: '#ffffff' }}
              />
            </div>
            <div>
              <Label className="text-slate-700">Fecha de Expiración (opcional)</Label>
              <Input
                type="date"
                value={newItem.fecha_expiracion}
                onChange={(e) => setNewItem({ ...newItem, fecha_expiracion: e.target.value })}
                style={{ backgroundColor: '#ffffff' }}
              />
              <p className="text-xs text-slate-500 mt-1">Dejar vacío para permanente</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} style={{ color: '#374151' }}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} style={{ backgroundColor: '#2563eb', color: '#ffffff' }}>
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Carga Masiva */}
      <BulkUploadModal
        open={showBulkDialog}
        onClose={() => setShowBulkDialog(false)}
        onSuccess={loadData}
        defaultTipo={filter.tipo !== 'all' ? filter.tipo : 'WHITELIST'}
      />
    </div>
  );
};

export default AccountLists;
